// Milestone Create/Edit dialog. Owns state + validation; delegates
// rendering to MilestoneFormFields. No Renew / no Generate Occurrences
// (milestones are non-recurring by design). Lifecycle transitions
// (delivered, accepted, paid) are handled by the inline expand ladder,
// not this modal.

import { useState, type ReactNode } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import MilestoneFormFields from "./MilestoneFormFields";
import {
  buildEmptyMilestoneFormState,
  buildMilestoneSuffixUpdatePatch,
  buildSowIdUpdatePatch,
  milestoneNumberInputError,
  nextMilestoneNumber,
  stateFromMilestone,
  toCreateMilestoneInput,
  type MilestoneFieldErrors,
  type MilestoneFormState,
} from "./milestoneFormState";
import { isValidIsoDate } from "../shared/isoDateValidation";
import useSeededFormState from "../../../../hooks/useSeededFormState";
import type {
  CreateMilestoneInput,
  Milestone,
  Sow,
  UpdateMilestoneInput,
} from "../../../../types";

export type MilestoneFormMode = "create" | "edit";

interface MilestoneFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: MilestoneFormMode;
  contractId: string;
  source?: Milestone;
  sows?: Sow[];
  /** Pre-fill `sowId` on Create. Used by the cross-tab "Add milestone
   *  to this SOW" flow from SowDetailExpand. Ignored on Edit. */
  initialSowId?: string;
  onCreate?: (input: CreateMilestoneInput) => Promise<unknown>;
  onUpdate?: (input: UpdateMilestoneInput) => Promise<unknown>;
  isSubmitting?: boolean;
  /** Existing milestones on the contract — used to auto-populate the
   *  Milestone # field on create (max + 1, or 1 if empty). The value
   *  stays editable so manual override (insertion between rows) works. */
  existingMilestones?: readonly Milestone[];
  /** Optional MilestoneDeliverablesPicker slot. The modal does not
   *  supply a default — the in-form deliverable picker has been moved to
   *  the inline-edit surface where the milestone always exists; on
   *  Create from the modal there is no milestone id to bind the picker
   *  to, so MilestoneFormFields renders the "save first" hint instead. */
  deliverablesSlot?: ReactNode;
}

function validate(state: MilestoneFormState): MilestoneFieldErrors {
  const errors: MilestoneFieldErrors = {};
  if (!state.name.trim()) errors.name = "Name is required";
  const numErr = milestoneNumberInputError(state.milestoneNumberInput);
  if (numErr) errors.milestoneNumberInput = numErr;
  if (!isValidIsoDate(state.plannedDate)) {
    errors.plannedDate = "Enter a valid planned date";
  }
  return errors;
}

const modeTitle: Record<MilestoneFormMode, string> = {
  create: "Add milestone",
  edit: "Edit milestone",
};

export default function MilestoneFormModal({
  isOpen,
  onClose,
  mode,
  contractId,
  source,
  sows,
  initialSowId,
  onCreate,
  onUpdate,
  isSubmitting,
  existingMilestones,
  deliverablesSlot,
}: MilestoneFormModalProps) {
  const [errors, setErrors] = useState<MilestoneFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Re-seed whenever the modal opens or its target changes.
  // existingMilestones.length is included in the seed key so the
  // auto-populated milestone number updates when milestones are added while
  // the create modal is open. The modal is eager-mounted (only isOpen
  // toggles) — useSeededFormState resets its guard on close, so reopening
  // with the same key (e.g. create → create) re-seeds instead of showing
  // the previous values.
  const [state, setState] = useSeededFormState<MilestoneFormState>(
    isOpen,
    `${mode}:${source?.id ?? "new"}:${initialSowId ?? ""}:${existingMilestones?.length ?? 0}`,
    () => {
      if (mode === "edit" && source) return stateFromMilestone(source);
      const empty = buildEmptyMilestoneFormState();
      return {
        ...empty,
        milestoneNumberInput: String(
          nextMilestoneNumber(existingMilestones ?? []),
        ),
        sowId: initialSowId ?? null,
      };
    },
    () => {
      setErrors({});
      setSubmitError(null);
    },
  );

  const handleChange = (patch: Partial<MilestoneFormState>) => {
    setState((prev) => ({ ...prev, ...patch }));
    const keys = Object.keys(patch);
    setErrors((prev) => {
      const next = { ...prev };
      for (const k of keys) {
        delete next[k as keyof MilestoneFieldErrors];
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate(state);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    setSubmitError(null);
    try {
      if (mode === "edit" && source && onUpdate) {
        const input: UpdateMilestoneInput = {
          version: source.version,
          ...toCreateMilestoneInput(state, contractId),
          ...buildSowIdUpdatePatch(state, source),
          ...buildMilestoneSuffixUpdatePatch(state, source),
        };
        await onUpdate(input);
      } else if (mode === "create" && onCreate) {
        await onCreate(toCreateMilestoneInput(state, contractId));
      }
      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to save milestone.",
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modeTitle[mode]}
      maxWidth="sm:max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <MilestoneFormFields
          value={state}
          onChange={handleChange}
          errors={errors}
          idPrefix={`ms-${mode}`}
          disabled={isSubmitting}
          sows={sows}
          deliverablesSlot={deliverablesSlot}
        />
        {submitError && <Alert variant="error">{submitError}</Alert>}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
            {mode === "edit" ? "Save changes" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
